import {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
} from "@mui/x-data-grid";

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const toDate = (value: unknown) => {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value as any);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getComparableDate = (value: unknown) => {
  const date = toDate(value);
  if (!date) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
};

const getRowValue = (row: any, column: GridColDef) => {
  if (typeof column.valueGetter === "function") {
    return (column.valueGetter as any)(
      row[column.field],
      row,
      column,
      undefined,
    );
  }

  return row[column.field];
};

const isEmptyFilterValue = (value: unknown) =>
  value == null || value === "" || (Array.isArray(value) && value.length === 0);

const matchesFilterItem = (
  row: any,
  item: GridFilterItem,
  columns: readonly GridColDef[],
) => {
  const column = columns.find((col) => col.field === item.field);
  if (!column) return true;

  const rowValue = getRowValue(row, column);
  const rowText = normalize(rowValue);
  const filterText = normalize(item.value);
  const operator = item.operator;

  if (["isEmpty", "isNotEmpty"].includes(operator)) {
    const empty = rowValue == null || rowValue === "";
    return operator === "isEmpty" ? empty : !empty;
  }

  if (["isAnyOf", "notAnyOf"].includes(operator)) {
    const values = Array.isArray(item.value) ? item.value : [item.value];
    const matches = values.some((value) => rowText === normalize(value));
    return operator === "isAnyOf" ? matches : !matches;
  }

  if (isEmptyFilterValue(item.value)) return true;

  if (
    column.type === "number" &&
    ["=", "!=", ">", ">=", "<", "<="].includes(operator)
  ) {
    const rowNumber = Number(rowValue);
    const filterNumber = Number(item.value);
    if (Number.isNaN(rowNumber) || Number.isNaN(filterNumber)) return false;

    switch (operator) {
      case "=":
        return rowNumber === filterNumber;
      case "!=":
        return rowNumber !== filterNumber;
      case ">":
        return rowNumber > filterNumber;
      case ">=":
        return rowNumber >= filterNumber;
      case "<":
        return rowNumber < filterNumber;
      case "<=":
        return rowNumber <= filterNumber;
      default:
        return true;
    }
  }

  if (
    ["date", "dateTime"].includes(String(column.type)) &&
    ["is", "not", "after", "onOrAfter", "before", "onOrBefore"].includes(
      operator,
    )
  ) {
    const rowDate = getComparableDate(rowValue);
    const filterDate = getComparableDate(item.value);
    if (rowDate == null || filterDate == null) return false;

    switch (operator) {
      case "is":
        return rowDate === filterDate;
      case "not":
        return rowDate !== filterDate;
      case "after":
        return rowDate > filterDate;
      case "onOrAfter":
        return rowDate >= filterDate;
      case "before":
        return rowDate < filterDate;
      case "onOrBefore":
        return rowDate <= filterDate;
      default:
        return true;
    }
  }

  switch (operator) {
    case "equals":
    case "is":
      return rowText === filterText;
    case "doesNotEqual":
    case "not":
      return rowText !== filterText;
    case "startsWith":
      return rowText.startsWith(filterText);
    case "endsWith":
      return rowText.endsWith(filterText);
    case "doesNotContain":
      return !rowText.includes(filterText);
    case "contains":
    default:
      return rowText.includes(filterText);
  }
};

const rowMatchesQuickFilter = (
  row: any,
  quickFilterValue: unknown,
  columns: readonly GridColDef[],
) => {
  const quickText = normalize(quickFilterValue);
  if (!quickText) return true;

  return columns.some((column) =>
    normalize(getRowValue(row, column)).includes(quickText),
  );
};

export const applyGridFilterModel = <Row extends any>(
  rows: readonly Row[],
  columns: readonly GridColDef[],
  filterModel: GridFilterModel,
): Row[] => {
  const items = filterModel.items.filter((item) => item.field && item.operator);
  const quickFilterValues =
    filterModel.quickFilterValues?.filter(Boolean) ?? [];

  if (items.length === 0 && quickFilterValues.length === 0) {
    return [...rows];
  }

  const itemLogicOperator = filterModel.logicOperator ?? GridLogicOperator.And;
  const quickFilterLogicOperator =
    filterModel.quickFilterLogicOperator ?? GridLogicOperator.And;

  return rows.filter((row) => {
    const itemResults = items.map((item) =>
      matchesFilterItem(row, item, columns),
    );
    const itemsMatch =
      itemResults.length === 0 ||
      (itemLogicOperator === GridLogicOperator.Or
        ? itemResults.some(Boolean)
        : itemResults.every(Boolean));

    if (!itemsMatch) return false;

    const quickResults = quickFilterValues.map((value) =>
      rowMatchesQuickFilter(row, value, columns),
    );

    return (
      quickResults.length === 0 ||
      (quickFilterLogicOperator === GridLogicOperator.Or
        ? quickResults.some(Boolean)
        : quickResults.every(Boolean))
    );
  });
};
